import React from 'react';
import Head from 'next/head';
import get from 'lodash/get';
import BaseHead, { blogPageMeta } from 'app/components/blog/BaseHead';
import { useAmp } from 'next/amp';
import { Article } from 'entity/blog/blogTypes';
import he from 'he';

const { locale, url, publisher } = blogPageMeta;
const IMAGE_WIDTH = 1200;
const IMAGE_HEIGHT = 628;

const PageHead = (props: { article: Article }) => {
  const { article } = props;

  // TODO: Update to use image caption, width and height values from api
  const isAmp = useAmp();

  const {
    slug,
    excerpt = '',
    publishDate = new Date(),
    categories = [],
    imageUrl = '',
    title = '',
    author,
    modifiedDate = new Date(),
    meta,
  } = article;

  const description = get(meta, 'description', '');
  const dateModified = new Date(modifiedDate).toISOString();
  const datePublished = new Date(publishDate).toISOString();
  const articleUrl = `${url}${slug}/`;
  let canonicalUrl;
  switch (articleUrl) {
    case 'https://blog.seedly.sg/tampines-central-vs-jurong-east/':
      canonicalUrl =
        'https://www.99.co/blog/singapore/tampines-central-vs-jurong-east-which-is-better/';
      break;
    case 'https://blog.seedly.sg/high-private-home-prices-sustainable/':
      canonicalUrl =
        'https://www.99.co/blog/singapore/private-home-prices-are-at-a-5-year-high-but-are-they-sustainable/';
      break;
    case 'https://blog.seedly.sg/mistakes-singaporeans-make-when-refinancing/':
      canonicalUrl =
        'https://www.99.co/blog/singapore/5-common-mistakes-singaporeans-make-when-refinancing/';
      break;
    case 'https://blog.seedly.sg/property-hunting-secret-experts-use/':
      canonicalUrl =
        'https://www.99.co/blog/singapore/3-property-hunting-secrets-that-expert-investors-use/';
      break;
    case 'https://blog.seedly.sg/own-x-properties-in-2-years-with-no-money-down-is-it-a-scam/':
      canonicalUrl =
        'https://www.99.co/blog/singapore/can-you-really-own-20-properties-with-no-cash-down/';
      break;
    case 'https://blog.seedly.sg/november-2019-bto-launches-brutally-ranked/':
      canonicalUrl =
        'https://www.99.co/blog/singapore/november-2019-bto-launches-brutally-ranked/';
      break;
    case 'https://blog.seedly.sg/below-35-single-housing-options/':
      canonicalUrl =
        'https://www.99.co/blog/singapore/below-35-single-desperate-and-looking-to-move-out-how-ah/';
      break;
    default:
      canonicalUrl = articleUrl;
  }
  const categoryName = get(categories, '[0].name', '');
  const encodedCategoryName = categoryName || he.encode(categoryName);
  const authorUrl = `${url}author/${author?.slug}/`;
  const encodedTitle = title ? he.encode(title) : title;
  const authorName = author && author.name ? he.encode(author.name) : '';
  const authorDesc =
    author && author.description ? he.encode(author.description) : '';

  const newsArticleSlug = [
    'singapore-presidential-elections-voting-guide-2023',
    'singapore-budget-summary-ministry-of-finance',
  ];

  const ldJson = {
    '@graph': [
      {
        '@type': 'ImageObject',
        '@id': `${articleUrl}#primaryimage`,
        url: imageUrl,
        width: IMAGE_WIDTH,
        height: IMAGE_HEIGHT,
        caption: encodedTitle,
      },
      {
        '@type': 'WebPage',
        '@id': `${articleUrl}#webpage`,
        url: articleUrl,
        inLanguage: locale,
        name: encodedTitle,
        isPartOf: {
          '@id': `${url}#website`,
        },
        image: {
          '@type': 'ImageObject',
          '@id': `${articleUrl}#primaryimage`,
          url: imageUrl,
          width: IMAGE_WIDTH,
          height: IMAGE_HEIGHT,
          caption: '',
        },
        primaryImageOfPage: {
          '@id': `${articleUrl}#primaryimage`,
        },
        datePublished,
        dateModified,
        description,
      },
      {
        '@context': 'http://schema.org',
        '@type': newsArticleSlug.includes(slug) ? 'NewsArticle' : 'BlogPosting',
        isPartOf: {
          '@id': `${articleUrl}#webpage`,
        },
        author: {
          '@id': `${authorUrl}#author`,
          name: authorName,
        },
        publisher: {
          '@id': `${url}#organization`,
        },
        headline: encodedTitle,
        datePublished,
        dateModified,
        commentCount: 0,
        image: {
          '@id': `${articleUrl}#primaryimage`,
        },
        articleSection: encodedCategoryName,
      },
      {
        '@type': 'Person',
        '@id': `${authorUrl}#author`,
        name: authorName,
        image: {
          '@type': 'ImageObject',
          '@id': `${url}#personlogo`,
          url: author?.imageUrl,
          caption: authorName,
        },
        description: authorDesc,
        sameAs: [],
      },
    ],
  };
  return (
    <Head>
      <title>{title}</title>
      <link rel="canonical" href={canonicalUrl} />
      <meta name="description" content={description} />
      <meta property="og:type" content="article" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={articleUrl} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:secure_url" content={imageUrl} />
      <meta property="og:image:width" content={IMAGE_WIDTH.toString()} />
      <meta property="og:image:height" content={IMAGE_HEIGHT.toString()} />
      <meta property="og:updated_time" content={dateModified} />
      <meta property="article:publisher" content={publisher} />
      <meta property="article:section" content={categoryName} />
      <meta property="article:published_time" content={datePublished} />
      <meta property="article:modified_time" content={dateModified} />
      <meta property="twitter:description" content={excerpt} />
      <meta property="twitter:title" content={title} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: BaseHead.buildLdJson(ldJson),
        }}
      />
      {isAmp && (
        <>
          {/* <script
            async
            custom-element='amp-script'
            src='https://cdn.ampproject.org/v0/amp-script-0.1.js'
          ></script> */}
          <script
            custom-element="amp-analytics"
            src="https://cdn.ampproject.org/v0/amp-analytics-0.1.js"
            async
          />
          {/* <script
            async
            custom-element='amp-position-observer'
            src='https://cdn.ampproject.org/v0/amp-position-observer-0.1.js'
          />

          <script
            async
            custom-element='amp-animation'
            src='https://cdn.ampproject.org/v0/amp-animation-0.1.js'
          /> */}
          <script
            async
            custom-element="amp-iframe"
            src="https://cdn.ampproject.org/v0/amp-iframe-0.1.js"
          />
        </>
      )}
    </Head>
  );
};

export default PageHead;
